import { guessTimeOfDayTitle } from '@shared/common/time-of-day/time-of-day';
import type { BookingType } from '@shared/modules/booking-types/booking-type';
import { BookingTypePresenter } from '@shared/modules/booking-types/booking-type-presenter';
import { ClientMedicationPresenter } from '@shared/modules/client-medication/client-medication.presenter';
import { ClientScheduleEntryPresenter } from '@shared/modules/client-schedule-entries/client-schedule-entry.presenter';
import {
    ClientTaskCompletionPresenter
} from '@shared/modules/client-task-completions/client-task-completion.presenter';
import type {
    ClientTaskScheduleEntry } from '@shared/modules/client-task-schedule-entries/client-task-schedule-entry';
import {
    ClientTaskScheduleEntryType
} from '@shared/modules/client-task-schedule-entries/client-task-schedule-entry';
import { ClientPresenter } from '@shared/modules/clients/client.presenter';
import { ScheduleEntryPresenter } from '@shared/modules/schedule/schedule-entry.presenter';
import { DateTime } from 'luxon';

export interface ScheduledDay {
    day: number;
    week: number;
    label: string;
    tooltip: string;
    enabled: boolean;
}

export class ClientTaskScheduleEntryPresenter extends ScheduleEntryPresenter<ClientTaskScheduleEntry> {
    get client(): ClientPresenter {
        return this.present('client.data', ClientPresenter);
    }

    get lastCompletion(): ClientTaskCompletionPresenter {
        return this.present('last_completion.data', ClientTaskCompletionPresenter);
    }

    get medication(): ClientMedicationPresenter {
        return this.present('client_medication.data', ClientMedicationPresenter);
    }

    get type() {
        return this._get('type', () => {
            switch (this.data.type) {
                case ClientTaskScheduleEntryType.medication:
                    return 'Medication';

                case ClientTaskScheduleEntryType.task:
                    return 'Task';
            }
        });
    }

    get dosage() {
        return this._data.medication_dosage;
    }

    get isTask() {
        return this._data.type === ClientTaskScheduleEntryType.task;
    }

    get isMedication() {
        return this._data.type === 'medication';
    }

    get title() {
        return this._get('title', () => {
            if (this.isMedication) {
                return this.medication?.name ?? this.content;
            }

            return this.content;
        });
    }

    get content(): string {
        return this._data.content;
    }

    get timeOfDay() {
        return this._get('timeOfDay', () => guessTimeOfDayTitle(this.data.start));
    }

    get start() {
        return this._get('start', () => this.formatTime(DateTime.fromFormat(this.data.start, 'HH:mm:ss')));
    }

    get finish() {
        return this._get('finish', () => this.formatTime(DateTime.fromFormat(this.data.finish, 'HH:mm:ss')));
    }

    get _effectiveAppliesFrom() {
        return this.toDateTime('effective_applies_from');
    }

    get _effectiveAppliesUntil() {
        return this.toDateTime('effective_applies_until');
    }

    get isInactive(): boolean {
        return this._get('isInactive', () => {
            if (this.isDeleted) {
                return false;
            }

            const appliesUntil = this._effectiveAppliesUntil;

            return !!(appliesUntil && appliesUntil < DateTime.local().startOf('day'));
        });
    }

    get isActive(): boolean {
        return this._get('isActive', () => {
            if (this.isDeleted) {
                return false;
            }

            const appliesFrom = this._effectiveAppliesFrom.toISODate();
            const appliesUntil = this._effectiveAppliesUntil?.toISODate();
            const today = DateTime.local().startOf('day').toISODate();

            if (appliesUntil) {
                return appliesFrom <= today
                    && appliesUntil >= today;
            }

            return appliesFrom <= today;
        });
    }

    get isPending(): boolean {
        return this._get('isPending', () => {
            if (this.isDeleted) {
                return false;
            }

            if (!this.isActive) {
                return this._effectiveAppliesFrom > DateTime.local();
            }

            return false;
        });
    }

    get applicableDatesTooltip(): string {
        return this._get('applicableDatesTooltip', () => {
            const appliesFrom = this._effectiveAppliesFrom;
            const appliesUntil = this._effectiveAppliesUntil;

            if (appliesFrom && appliesUntil) {
                return `${this.formatDate(appliesFrom)} - ${this.formatDate(appliesUntil)}`;
            }

            if (appliesFrom) {
                return `from ${this.formatDate(appliesFrom)}`;
            }

            if (appliesUntil) {
                return `until ${this.formatDate(appliesUntil)}`;
            }
        });
    }

    get bookingTypes(): BookingTypePresenter[] {
        return this.presentCollection<BookingType, BookingTypePresenter>('booking_types.data', BookingTypePresenter);
    }

    get bookingTypeNames(): string[] {
        return this._get('bookingTypeNames', () => {
            const bookingTypes = this.bookingTypes ?? [];

            return bookingTypes.map(t => t.name);
        });
    }

    get linkedTo(): ClientScheduleEntryPresenter {
        return this.present('linked_to.data', ClientScheduleEntryPresenter);
    }

    get linkedToTitle(): string {
        return this._get('linkedToTitle', () => this.linkedTo?.describeWhen);
    }

    get linkedToTooltip(): string {
        return this._get('linkedToTooltip', () => {
            let tooltip = `Linked to ${this.linkedTo.title}`;

            if (this.linkedTo.isDeleted) {
                tooltip += ' - DELETED';
            }
            return tooltip;
        });
    }
}
